<template>
  <transition-group name="timer-dock" tag="div" class="timer-dock" dir="rtl">
    <div
      v-for="timer in running"
      :key="timer.taskId"
      class="timer-dock__row"
      :class="{ 'is-busy': busy[timer.taskId] }"
    >
      <span class="timer-dock__live"></span>
      <button
        type="button"
        class="timer-dock__title"
        :title="timer.title"
        @click="openTasks"
      >
        {{ timer.title || label.form.task }}
      </button>
      <span class="timer-dock__time">{{ timeText(timer) }}</span>
      <button
        type="button"
        class="timer-dock__stop"
        :title="label.button.stopTimer"
        :disabled="!!busy[timer.taskId]"
        @click="stop(timer)"
      >
        <v-icon x-small>mdi-stop</v-icon>
      </button>
    </div>
  </transition-group>
</template>

<script>
import { mapGetters } from "vuex";
import label from "@/lang/StaticLabels.js";
import { formatTimer, timerSeconds } from "./taskTimer.js";

export default {
  name: "TaskTimerDock",
  data() {
    return {
      label,
      now: Date.now(),
      tick: null,
      busy: {},
    };
  },
  computed: {
    ...mapGetters({
      timers: "phamyarX/taskTimers",
    }),
    running() {
      return Object.values(this.timers).filter((timer) => timer.isRunning);
    },
  },
  watch: {
    "running.length": {
      immediate: true,
      handler(count) {
        this.now = Date.now();
        if (count && !this.tick) {
          this.tick = setInterval(() => {
            this.now = Date.now();
          }, 1000);
        } else if (!count && this.tick) {
          clearInterval(this.tick);
          this.tick = null;
        }
      },
    },
  },
  methods: {
    timeText(timer) {
      return formatTimer(timerSeconds(timer, this.now));
    },
    stop(timer) {
      const id = timer.taskId;
      if (this.busy[id]) return;
      this.$set(this.busy, id, true);
      this.$store
        .dispatch("phamyarX/toggleTaskTimer", { id, title: timer.title })
        .finally(() => this.$delete(this.busy, id));
    },
    openTasks() {
      if (this.$route.name === "PHAMYARTaskList") return;
      this.$router.push({ name: "PHAMYARTaskList" }).catch(() => null);
    },
  },
  beforeDestroy() {
    clearInterval(this.tick);
  },
};
</script>

<style lang="scss" scoped>
$first: var(--color-first-r), var(--color-first-g), var(--color-first-b);
$success: var(--color-success-r), var(--color-success-g), var(--color-success-b);
$danger: var(--color-danger-r), var(--color-danger-g), var(--color-danger-b);

.timer-dock {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: var(--font-family-main);
  text-align: start;

  &:not(:empty) {
    margin-bottom: 8px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 6px 0 10px;
    border-radius: 10px;
    border: 1px solid rgba(#{$success}, 0.28);
    background: linear-gradient(
      90deg,
      rgba(#{$success}, 0.1),
      rgba(#{$first}, 0.04)
    );
    transition: opacity 0.2s ease;

    &.is-busy {
      opacity: 0.6;
    }
  }

  // نقطه‌ی زنده با موج
  &__live {
    position: relative;
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-success);
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: var(--color-success);
      animation: timer-dock-ping 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
    }
  }

  &__title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: start;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--color-dark);
    cursor: pointer;
    &:hover {
      color: var(--color-first);
    }
  }

  &__time {
    flex: 0 0 auto;
    font-size: 12.5px;
    font-weight: 700;
    color: var(--color-success);
    font-variant-numeric: tabular-nums;
    direction: ltr;
  }

  &__stop {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(#{$danger}, 0.1);
    transition: background 0.2s ease;
    .v-icon {
      color: var(--color-danger) !important;
    }
    &:hover:not(:disabled) {
      background: var(--color-danger);
      .v-icon {
        color: #fff !important;
      }
    }
    &:disabled {
      cursor: default;
    }
  }
}

.timer-dock-enter-active,
.timer-dock-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.timer-dock-enter,
.timer-dock-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@keyframes timer-dock-ping {
  75%,
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .timer-dock__live::after {
    animation: none;
  }
}
</style>
